import React from 'react';
import { Card, Col, Row, Statistic, Empty, Spin } from 'antd';
import { FormattedMessage } from 'react-intl';
import { useGetBrandEquityIntegrityComparisonQuery } from '../../../services/nestjs-base-api';
import { useAuth } from '../../../hooks/useAuth';

interface BrandEquityComparisonProps {
    brandId: number | null | undefined;
    period?: string;
    dateFrom?: string;
    dateTo?: string;
}

export const BrandEquityComparison: React.FC<BrandEquityComparisonProps> = ({
    brandId,
    period,
    dateFrom,
    dateTo,
}) => {
    const { user } = useAuth();
    const { data, isLoading } = useGetBrandEquityIntegrityComparisonQuery(
        {
            jwtToken: user?.jwtToken || '',
            brandId: brandId as number,
            period,
            dateFrom,
            dateTo,
        },
        { skip: !brandId || !user?.jwtToken },
    );

    if (!brandId) {
        return null;
    }
    if (isLoading) {
        return (
            <Card>
                <Spin />
            </Card>
        );
    }
    if (!data) {
        return (
            <Card>
                <Empty
                    description={<FormattedMessage id="responseIntegrity.beComparison.empty" />}
                />
            </Card>
        );
    }

    const rawScore = data.raw?.overall;
    const postScore = data.postReview?.overall;
    const delta = data.delta;
    const deltaPositive = typeof delta === 'number' && delta > 0;
    const deltaNegative = typeof delta === 'number' && delta < 0;

    return (
        <Card
            title={<FormattedMessage id="responseIntegrity.beComparison.title" />}
        >
            <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                    <Statistic
                        title={
                            <FormattedMessage id="responseIntegrity.beComparison.rawLabel" />
                        }
                        value={typeof rawScore === 'number' ? rawScore.toFixed(1) : '—'}
                        suffix={
                            <span style={{ fontSize: 12, color: '#999' }}>
                                {' / 100'}
                            </span>
                        }
                    />
                    <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                        <FormattedMessage
                            id="responseIntegrity.beComparison.responseCount"
                            values={{ count: data.raw?.responseCount ?? 0 }}
                        />
                    </div>
                </Col>
                <Col xs={24} md={8}>
                    <Statistic
                        title={
                            <FormattedMessage id="responseIntegrity.beComparison.postLabel" />
                        }
                        value={typeof postScore === 'number' ? postScore.toFixed(1) : '—'}
                        suffix={
                            <span style={{ fontSize: 12, color: '#999' }}>
                                {' / 100'}
                            </span>
                        }
                    />
                    <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                        <FormattedMessage
                            id="responseIntegrity.beComparison.responseCount"
                            values={{ count: data.postReview?.responseCount ?? 0 }}
                        />
                        {' · '}
                        <FormattedMessage
                            id="responseIntegrity.beComparison.rejectedCount"
                            values={{ count: data.rejectedCount ?? 0 }}
                        />
                    </div>
                </Col>
                <Col xs={24} md={8}>
                    <Statistic
                        title={
                            <FormattedMessage id="responseIntegrity.beComparison.delta" />
                        }
                        value={
                            typeof delta === 'number'
                                ? `${delta > 0 ? '+' : ''}${delta.toFixed(2)}`
                                : '—'
                        }
                        valueStyle={{
                            color: deltaPositive
                                ? '#52c41a'
                                : deltaNegative
                                  ? '#fa541c'
                                  : undefined,
                        }}
                    />
                </Col>
            </Row>
        </Card>
    );
};
